import { Box, Stack, Typography } from '@atgfr8/atg-ui-tailwind';
import {
  BasicModal,
  BasicTable,
  Button,
  CardPlaceholder,
  ToastType,
} from '@components';
import { AtgLoader } from '@components/AtgLoader';
import { approveCheckRequest, getAllCheckRequest } from '@store/Global/service';
import {
  CheckRequestType,
  PendingChecksTypes,
  PreviousCheckTypes,
} from '@store/Global/types';
import { setSidebarDataStore, sidebarDataStore } from '@store/index';
import { closeModal, openModal } from '@store/modals';
import { formatAmount, handleToast } from '@utils/utils';
import { DateTime } from 'luxon';
import { createSignal, Show } from 'solid-js';

import DenyCheckRequest from './DenycheckRequest';

const Advances = () => {
  const [requestCheckData, setRequestData] = createSignal<CheckRequestType>();
  const [requestCheckLoading, setRequestCheckLoading] = createSignal(false);
  const [submitLoading, setSubmitLoading] = createSignal(false);

  const advancesColumns = [
    {
      name: 'Load #',
      key: 'loadNum',
    },
    {
      name: 'Agent',
      key: 'requestedBy',
    },
    {
      name: 'Amount',
      key: 'requestedBy',
      renderCell: (row: PendingChecksTypes) => (
        <>{`${formatAmount(row.for)}`}</>
      ),
    },
    {
      name: 'Details',
      key: 'details',
    },
    {
      name: 'Date',
      key: 'requestDate',
      renderCell: (row: PendingChecksTypes) => (
        <>{`${DateTime.fromISO(row.requestDate).toFormat('MM/dd/yyyy')}`}</>
      ),
    },
  ];

  const pendingChecksColumn = [
    {
      name: 'Load',
      key: 'loadNum',
    },
    {
      name: 'Customer',
      key: 'customer',
    },
    {
      name: 'Agent',
      key: 'agent',
    },
    {
      name: 'Loads Completed',
      key: 'loadsCompleted',
    },
  ];

  const previousIssueCheck = [
    {
      name: 'Amount',
      key: 'amount',
      renderCell: (row: PreviousCheckTypes) => (
        <>{`${formatAmount(row.amount)}`}</>
      ),
    },
    {
      name: 'Description',
      key: 'description',
    },
    {
      name: 'Date',
      key: 'date',
      renderCell: (row: PreviousCheckTypes) => (
        <>{`${
          row.date ? DateTime.fromISO(row.date).toFormat('MM/dd/yyyy') : ''
        }`}</>
      ),
    },
  ];

  const footerRow = [
    {
      renderCell: () => (
        <Typography variant="body1" class="text-sm! font-bold! text-black!">
          <span>Total: </span>
          {`${formatAmount(
            requestCheckData()?.previousChecks.reduce((acc, cur) => {
              return acc + cur.amount;
            }, 0),
          )}/${formatAmount(requestCheckData()?.carrierPay)}`}
        </Typography>
      ),
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
  ];

  const onRowClick = async (row: PendingChecksTypes) => {
    setRequestCheckLoading(true);
    openModal('pending-checks');
    const res = await getAllCheckRequest(row.id);
    if (res && res.isSuccess) {
      setRequestData(res.value);
    }
    setRequestCheckLoading(false);
  };

  return (
    <Box class="w-[700px] max-w-full bg-white p-2">
      <Show when={requestCheckLoading()}>
        <AtgLoader />
      </Show>
      <Show
        when={!sidebarDataStore.pendingChecksData.loading}
        fallback={<CardPlaceholder />}
      >
        <Box
          class={`
            min-h-[calc(100vh-300px)]! max-h-[calc(100vh-250px)]! [&>div>table>.MuiTableHead-root]:bg-[#F2F2F2]
            [&>div>table>.MuiTableBody-root>.MuiTableRow-root]:cursor-pointer h-auto overflow-y-auto
          `}
        >
          <BasicTable<PendingChecksTypes>
            columns={advancesColumns}
            rows={sidebarDataStore.pendingChecksData.data}
            onRowClick={onRowClick}
            tableContainerClasses="text-black! [&_td]:!border-[#c4c4c4] [&_th]:!border-[#c4c4c4]"
            tableHeaderClasses="bg-[#f2f2f2]"
            tableBodyRowClasses="cursor-pointer"
          />
        </Box>
      </Show>
      <BasicModal
        title="Pending Checks"
        id={'pending-checks'}
        onClose={() => {
          closeModal('pending-checks');
        }}
        showClose={true}
        width={'900px'}
        footer={false}
      >
        <Show when={!requestCheckLoading()} fallback={<CardPlaceholder />}>
          <Box class="h-auto max-h-[580px] overflow-y-auto [&>div>div>table>.MuiTableHead-root]:bg-[#F2F2F2]">
            <Stack spacing={2}>
              <BasicTable<CheckRequestType>
                columns={pendingChecksColumn}
                rows={[requestCheckData() as CheckRequestType]}
                tableContainerClasses="text-black! [&_td]:!border-[#c4c4c4] [&_th]:!border-[#c4c4c4]"
                tableHeaderClasses="bg-[#f2f2f2]"
              />
              <Typography variant="h6" class="text-xl! font-bold!">
                For: {requestCheckData()?.carrier}
              </Typography>
              <Typography
                variant="h6"
                class="text-xs! font-normal! text-[#808080]"
              >
                Amount Requested:{' '}
                <span class="text-base! font-bold! text-black!">
                  {formatAmount(requestCheckData()?.amount)}
                </span>
              </Typography>
              <Typography variant="h6" class="text-xl! font-medium!">
                Previous Issued Checks
              </Typography>
              <BasicTable<PreviousCheckTypes>
                columns={previousIssueCheck}
                rows={requestCheckData()?.previousChecks}
                footerRow={footerRow}
                tableContainerClasses="text-black! [&_td]:!border-[#c4c4c4] [&_th]:!border-[#c4c4c4]"
                tableHeaderClasses="bg-[#f2f2f2]"
              />
              <Box class="flex justify-end gap-2">
                <>
                  <Button
                    disabled={submitLoading()}
                    isLoading={submitLoading()}
                    class="bg-[#248670]! rounded-xs!"
                    variant="contained"
                    onClick={async () => {
                      try {
                        setSubmitLoading(true);
                        const response = await approveCheckRequest(
                          requestCheckData()?.requestId as number,
                        );

                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                        if (response && response.success) {
                          handleToast(
                            ToastType.Success,
                            'Approved check request successfully',
                          );
                          const filteredData =
                            sidebarDataStore.annoucementsData.data.filter(
                              (item) =>
                                item.id !== requestCheckData()?.requestId,
                            );
                          setSidebarDataStore(
                            'annoucementsData',
                            'data',
                            filteredData,
                          );
                          setRequestData();
                          closeModal('pending-checks');
                        } else {
                          handleToast(
                            ToastType.Error,
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            response.message ?? 'Error approving check request',
                          );
                        }
                      } catch (error) {
                        if (error instanceof Error) {
                          handleToast(ToastType.Error, error.message);
                        }
                      } finally {
                        setSubmitLoading(false);
                      }
                    }}
                    type="button"
                    label="Approve"
                    dataTestId="approve-pending-checks-button-sidebar"
                  />
                  <Button
                    disabled={submitLoading()}
                    isLoading={submitLoading()}
                    class="bg-[#B00020]! rounded-xs!"
                    variant="contained"
                    onClick={() => openModal('deny-pending-checks')}
                    type="button"
                    label="Deny"
                    dataTestId="deny-pending-checks-button-sidebar"
                  />
                </>
              </Box>
            </Stack>
          </Box>
        </Show>
      </BasicModal>
      <DenyCheckRequest requestCheckData={requestCheckData} />
    </Box>
  );
};

export default Advances;
